import React, { useContext, useEffect, useState } from "react";
import {
  Typography,
  FormLabel,
  MenuItem,
  TextField,
  FormGroup,
  Select,
} from '@mui/material';
import { Controller, useForm } from "react-hook-form";
import i18next from "i18next";
import { Context } from "../../../states/Store";
import { formatThirdpartyIdentifier, isEmpty } from '../../../services/common/Utils';
import { ERROR, getUserLanguages, REGEXP_EMAIL, UserInvitationStatus } from '../../../services/common/Constants';
import { checkUserExists, createAccountUsers } from "../../../services/edge/UserService";
import EADialog from "../../../components/common/EADialog";
import useStyles from "../../../style/js-style/containers/users/users-list/UserCreationDialogStyle";
import FeedBackPopUp from "../../../components/FeedBackPopUp";
import { createTPContacts } from "../../../services/edge/ThirdpartyService";
import _ from "lodash";
import AttributeMultipleSelectChips from "../../../components/AttributeMultipleSelectChips";
import {useRequestLoading} from "../../../components/common/hooks/useRequestLoading";
import EAColorIcon from '../../../components/common/EAColorIcon';
import EAIcon from '../../../components/common/EAIcon';

const UserCreationDialog = ({ isOpen, handleClose, handleOnCreate, tpContact = false, tpId, shownRoles = [], onReinviteUser, userType = "user", thirdparty }) => {
  const { classes } = useStyles();
  const { isRequestLoading, startLoading, stopLoading } = useRequestLoading();
  const [state, dispatch] = useContext(Context);
  const [isInvitationSend, setIsInvitationSend] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [confirmationLevel, setConfirmationLevel] = useState(0);
  const [disabledValidate, setDisabledValidate] = useState(false);
  const [isFirstValidate, setIsFirstValidate] = useState(true);
  const availableLanguages = getUserLanguages();
  const id = isOpen ? "filters-dialog" : undefined;
  const {
    control,
    formState: { errors },
    formState,
    handleSubmit,
    setError,
    setValue,
    trigger,
    getValues
  } = useForm({ mode: "onChange", delayError: 500 });

  useEffect(() => {
    !isFirstValidate && setDisabledValidate(!_.isEmpty(formState.errors.email))
    setIsFirstValidate(false);
  }, [formState, isFirstValidate]);

  useEffect(() => {
    //For now, the default selected language in the selector is the current language used on 365
    setValue("lang", state.lang);
  }, [setValue, tpContact, state.lang])

  const handleCreation = async (data) => {
    const result = await checkUserExists(state.account.id, tpContact ? tpId : state.account.id, data.email);
    const user = result?.content;
    if (user !== undefined && user.status !== UserInvitationStatus.EXPIRED_INVITATION) {
      if (user.status === UserInvitationStatus.INVITED) {
        handleClose();
        onReinviteUser(user);
      } else {
        setIsExistingUser(true);
      }
    } else {
      if (tpContact && isEmpty(errors)) {
        const contactToCreate = {
          email: data.email,
          lang: data.lang
        }
        try {
          startLoading();
          await createTPContacts(state.account.id, tpId, contactToCreate, null);
          setIsInvitationSend(true);
          handleOnCreate();
          setConfirmationLevel(2);
        } catch (error) {
          let errorMessage = error?.message?.messages?.error?.[0]?.message;
          let codeMessage = error?.message?.messages?.error?.[0]?.code;
          if (errorMessage?.includes("Blacklisted")) {
            setError('email', { type: 'custom', message: i18next.t("infos.EMAIL_BLACKLISTED_ERROR") });
            setConfirmationLevel(0);
          } else if (errorMessage?.includes("This third party does not wish to have any additional contacts added to their account")) {
            setError('email', { type: 'custom', message: i18next.t("infos.COMPTE_BLACKLISTED_ERROR") });
            setConfirmationLevel(0);
          } else if (errorMessage?.includes("Invalid email address")) {
            setError('email', { type: 'custom', message: i18next.t("infos.E030") });
            setConfirmationLevel(0);
          } else if (codeMessage === 'E133') {
            const insideParentheses = errorMessage.match(/\(([^)]+)\)/)?.[1] ?? 'unknown_reason';
            setError('email', { type: 'custom', message: i18next.t('invalidEmail', { details: insideParentheses }) });
            setConfirmationLevel(0);
          } else {
            dispatch({ type: "ALERT", alert: { type: ERROR, msg: "USER_CREATION_ERROR" } });
          }
        } finally {
          stopLoading();
        }
      } else {
        const userToCreate = {
          roleIds: data.roleIds,
          email: data.email,
          lang: data.lang
        };
        try {
          await createAccountUsers(state.account.id, userToCreate, dispatch);
          setIsInvitationSend(true);
          handleOnCreate();
          setConfirmationLevel(2);
        } catch (error) {
          dispatch({
            type: "ALERT",
            alert: { type: ERROR, msg: "USER_CREATION_ERROR" },
          });
        }
      }
    }
  };

  const handleDialogClose = () => {
    handleClose();
    setConfirmationLevel(0);
  };

  const displayTitle = (tpContact, existingUser) => {
    if (existingUser) {
      return "";
    } else {
      return tpContact ? i18next.t("thirdpartySheet.inviteUserTitle") : i18next.t("usersTable.inviteUserTitle");
    }
  };

  const displayContent = (tpContact, existingUser) => {
    if (existingUser) {
      return i18next.t("usersTable.existingUser");
    } else {
      return tpContact ? i18next.t("usersTable.createdTPInfo") : i18next.t("usersTable.createdInfo");
    }
  };

  const handleFirstConfirmation = async () => {
    const inputMailError = _.isEmpty(formState.errors.email)
    const mailInput = getValues("email")

    if (!inputMailError || !mailInput) {
      trigger("email");
      setDisabledValidate(true);
    } else {
      setDisabledValidate(false);
      setConfirmationLevel(1);
    }
  }

  return (
    <EADialog
      id={id}
      isOpen={isOpen}
      onCancel={handleDialogClose}
      onValidate={
        userType === 'user' || confirmationLevel === 2
          ? handleSubmit(handleCreation)
          : confirmationLevel === 0
          ? handleFirstConfirmation
          : handleSubmit(handleCreation)
      }
      hasActions={
        !(confirmationLevel === 2 || isInvitationSend || isExistingUser)
      }
      maxWidth="popupxs"
      fullWidth
      btnSize="regular"
      isFeedBack={confirmationLevel === 2 || isInvitationSend || isExistingUser}
      customBtnStyle={{ paddingBottom: '24px !important' }}
      isValidateDisabled={disabledValidate || isRequestLoading}
    >
      {confirmationLevel === 0 && (
        <form>
          {/* Before confirm : pop up */}
          {!(isInvitationSend || isExistingUser) && (
            <>
              {/* Header */}
              <div className={classes.headerWrapper}>
                <div className={classes.imgWrapper}>
                  <EAColorIcon
                    icon={'add-user'}
                    svgStyle={{
                      width: '33px',
                      position: 'relative',
                      left: '-4px',
                    }}
                  />
                </div>
                <div>
                  <Typography variant="body1" className={classes.dialogText}>
                    {i18next.t('usersTable.creationInfo')}
                  </Typography>
                </div>
              </div>

              <div className={classes.formInputWrapper}>
                {/* Role label + input */}
                {!tpContact && !_.isEmpty(shownRoles) && (
                  <div className={classes.formContainer}>
                    <FormLabel className={classes.formLabel}>
                      {`${i18next.t('usersTable.roleSelectLabel')} * `}
                    </FormLabel>
                    <FormGroup>
                      <Controller
                        control={control}
                        name="roleIds"
                        defaultValue={[shownRoles[0].value]}
                        rules={{
                          required: i18next.t('infos.required'),
                        }}
                        render={({ field }) => (
                          <Select
                            className={classes.labelInputRole}
                            id="user-role-id"
                            variant="outlined"
                            multiple
                            {...field}
                            error={!!errors.roleIds}
                            renderValue={(selected) => (
                              <AttributeMultipleSelectChips
                                key={selected}
                                selected={selected}
                                setValue={setValue}
                                shownRoles={shownRoles}
                              />
                            )}
                          >
                            {shownRoles.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                className={classes.menuItem}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormGroup>
                    <span className={classes.errorText}>
                      {!!errors.roleIds ? errors.roleIds.message : ''}
                    </span>
                  </div>
                )}
                {/* Mail label + input */}
                <div className={classes.formContainer}>
                  <FormLabel className={classes.formLabel}>
                    {`${i18next.t('usersTable.emailInputLabel')} * `}
                  </FormLabel>
                  <FormGroup>
                    <Controller
                      control={control}
                      name="email"
                      rules={{
                        required: i18next.t('infos.required'),
                        pattern: {
                          value: REGEXP_EMAIL,
                          message: i18next.t('usersTable.emailInputError'),
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          className={classes.labelInputMail}
                          id="email"
                          variant="outlined"
                          error={!!errors.email}
                          fullWidth
                          placeholder={'john.doe@e-attestations.com'}
                          helperText={
                            !!errors.email ? errors.email.message : ''
                          }
                          {...field}
                        />
                      )}
                    />
                  </FormGroup>
                </div>
                {/* Lang label + input */}
                {!_.isEmpty(availableLanguages) && (
                  <div className={classes.formContainer}>
                    <FormLabel className={classes.formLabel}>
                      {`${i18next.t('usersTable.languageLabel')} * `}
                    </FormLabel>
                    <FormGroup>
                      <Controller
                        control={control}
                        name="lang"
                        defaultValue={[availableLanguages[0].value]}
                        rules={{
                          required: i18next.t('infos.required'),
                        }}
                        render={({ field }) => (
                          <Select
                            className={classes.labelInputMail}
                            id="user-lang"
                            variant="outlined"
                            {...field}
                            error={!!errors.lang}
                            renderValue={(selected) => (
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <EAIcon
                                  icon={'/flag/universal'}
                                  extension={'png'}
                                  className={classes.itemFlagImage}
                                />
                                {
                                  availableLanguages.filter(
                                    (lang) => lang.code === selected
                                  )?.[0]?.name
                                }
                              </span>
                            )}
                          >
                            {availableLanguages.map((option) => (
                              <MenuItem
                                key={option.code}
                                value={option.code}
                                className={classes.menuItem}
                              >
                                <>
                                  <EAIcon
                                    icon={'/flag/universal'}
                                    extension={'png'}
                                    className={classes.itemFlagImage}
                                  />
                                  {option.name}
                                </>
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormGroup>
                    <span className={classes.errorText}>
                      {!!errors.lang ? errors.lang.message : ''}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </form>
      )}

      {/* Double confirmation */}
      {confirmationLevel === 1 && (
        <div className={classes.doubleConfirmationWrapper}>
          <div className={classes.doubleConfirmationIconWrapper}>
            <EAIcon
              icon={'warning-rounded'}
              style={{ width: '36px', height: '36px' }}
            />
          </div>

          <div className={classes.doubleConfirmationTitle}>
            {i18next.t('contactsTable.inviteConfirmation', {
              contact: getValues('email'),
            })}
          </div>

          <div className={classes.informationsTitleWrapper}>
            <div className={classes.informationsEtablissement}>
              <EAColorIcon
                icon={'thirdparty_green'}
                svgStyle={{
                  width: '100%',
                  marginRight: '5px',
                  position: 'relative',
                  top: '2px',
                  flex: 1,
                }}
                width={'20px'}
                height={'20px'}
              />
              <div>
                {thirdparty.name}{' '}
                {formatThirdpartyIdentifier(thirdparty, true) &&
                  ' - ' + formatThirdpartyIdentifier(thirdparty, true)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* After user created : feedback */}
      {confirmationLevel === 2 && (
        <FeedBackPopUp
          onClick={handleDialogClose}
          title={displayTitle(tpContact, isExistingUser)}
          imgSrc={'add-user'}
          content={displayContent(tpContact, isExistingUser)}
          variant={'valid'}
        />
      )}
    </EADialog>
  );
};

export default UserCreationDialog;
